import { useEffect, useRef } from "react";

// Plain <video> for lessons whose video is a file we host ourselves, instead of
// a Bunny iframe. Same props as BunnyPlayer so LessonPage treats them alike.

type Props = {
  src: string;
  resumeAt: number;
  onProgress: (positionSec: number, durationSec: number) => void;
  /** Every player tick, unthrottled: feeds the watch map (which parts were played). */
  onTick?: (positionSec: number, durationSec: number) => void;
  throttleMs?: number;
};

export function FilePlayer({ src, resumeAt, onProgress, onTick, throttleMs = 30_000 }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastWriteRef = useRef(0);
  const seededRef = useRef(false);

  useEffect(() => {
    seededRef.current = false; // new lesson, allow one resume seek again
  }, [src]);

  return (
    <div className="lesson-video">
      <video
        ref={videoRef}
        src={src}
        controls
        playsInline
        preload="metadata"
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
        style={{ width: "100%", display: "block", background: "#000" }}
        onLoadedMetadata={() => {
          const v = videoRef.current;
          if (!v || seededRef.current) return;
          seededRef.current = true;
          // resume where they stopped, but never on top of the ending
          if (resumeAt > 0 && (!v.duration || resumeAt < v.duration - 5)) v.currentTime = resumeAt;
        }}
        onTimeUpdate={() => {
          const v = videoRef.current;
          if (!v) return;
          onTick?.(v.currentTime, v.duration || 0);
          const now = Date.now();
          if (now - lastWriteRef.current < throttleMs) return;
          lastWriteRef.current = now;
          onProgress(v.currentTime, v.duration || 0);
        }}
        onPause={() => {
          const v = videoRef.current;
          if (v) onProgress(v.currentTime, v.duration || 0);
        }}
        onEnded={() => {
          const v = videoRef.current;
          if (v) onProgress(v.duration || v.currentTime, v.duration || 0);
        }}
      />
    </div>
  );
}
